import { useState, useEffect } from "react";
import {
  InsightsCard,
  InsightsDividerList,
  InsightsDividerRow,
  MaskedValue,
  formatDate,
} from "./InsightsCard";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { decryptPersonData } from "@/shared/utils/serverEncryption";
import { sanitizeValue, toTitleCase } from "./insightUtils";
import { useCitation, getCitationForField } from "./CitationContext";

interface ExtractedContact {
  full_name?: string;
  first_name?: string;
  last_name?: string;
  relationship?: string;
  legal_role?: string[];
  contact_info?: {
    email?: string;
    phone?: string;
    address?: string;
  };
}

export interface DeathCertificateExtraction {
  decedent_information?: {
    full_name?: string | null;
    first_name?: string | null;
    middle_name?: string | null;
    last_name?: string | null;
    date_of_birth?: string | null;
    date_of_death?: string | null;
    place_of_death?: string | null;
    ssn?: string | null;
    marital_status?: string | null;
    age_at_death?: string | null;
    sex?: string | null;
    residence_address?: string | null;
  };
  administrative_information?: {
    certificate_number?: string | null;
    date_issued?: string | null;
    state_filed?: string | null;
    county_filed?: string | null;
    registrar_name?: string | null;
  };
  other_details?: {
    occupation?: string | null;
    birthplace?: string | null;
    education_level?: string | null;
    race?: string | null;
    armed_forces_service?: boolean | null;
    residence_address?: string | null;
  };
  surviving_spouse?: {
    name?: string | null;
  };
  parents?: {
    father_name?: string | null;
    mother_name?: string | null;
    mother_maiden_name?: string | null;
  };
  disposition?: {
    funeral_home?: string | null;
  };
  informant?: {
    name?: string | null;
    relationship?: string | null;
    address?: string | null;
  };
}

const NOT_SPECIFIED = "Not specified";

const findByRelationship = (contacts: ExtractedContact[] | undefined, relationship: string) =>
  contacts?.find((c) => c.relationship?.toLowerCase() === relationship);

const formatVeteranStatus = (served: boolean | null | undefined) =>
  served === true ? "Yes" : served === false ? "No" : NOT_SPECIFIED;

export default function DeathCertificateInsights({
  data,
  contacts,
  citations,
}: {
  data: DeathCertificateExtraction;
  contacts?: ExtractedContact[];
  citations?: Record<string, unknown> | null;
}) {
  const { showCitation } = useCitation();

  const citationClick = (fieldPath: string) => {
    const citation = getCitationForField(citations, fieldPath);
    return citation ? () => showCitation(citation) : undefined;
  };

  const {
    decedent_information,
    administrative_information,
    other_details,
    surviving_spouse,
    parents,
    disposition,
    informant,
  } = data;

  const { person } = useAppContext();
  const [decryptedSSN, setDecryptedSSN] = useState<string | null>(null);
  const [isLoadingSSN, setIsLoadingSSN] = useState(false);

  // Decrypt SSN from the parsed document
  useEffect(() => {
    const encryptedSSN = decedent_information?.ssn;

    if (!encryptedSSN || !person?.estate_id) {
      setDecryptedSSN(null);
      return;
    }

    const decryptSSN = async () => {
      setIsLoadingSSN(true);
      try {
        const decrypted = await decryptPersonData(person.estate_id, encryptedSSN, "ssn");
        setDecryptedSSN(decrypted);
      } catch (error) {
        console.error("Failed to decrypt SSN:", error);
        setDecryptedSSN(null);
      } finally {
        setIsLoadingSSN(false);
      }
    };

    decryptSSN();
  }, [decedent_information?.ssn, person?.estate_id]);

  const stateCountyFiled = [
    toTitleCase(sanitizeValue(administrative_information?.county_filed)),
    toTitleCase(sanitizeValue(administrative_information?.state_filed)),
  ]
    .filter(Boolean)
    .join(", ");

  const deceasedContact = findByRelationship(contacts, "deceased");
  const spouseContact = findByRelationship(contacts, "spouse");

  const fullLegalName =
    sanitizeValue(decedent_information?.full_name) || sanitizeValue(deceasedContact?.full_name);
  const spouseName = toTitleCase(
    sanitizeValue(surviving_spouse?.name) || sanitizeValue(spouseContact?.full_name),
  );
  const residencePath = sanitizeValue(decedent_information?.residence_address)
    ? "decedent_information.residence_address"
    : "other_details.residence_address";

  const displayName =
    toTitleCase(sanitizeValue(decedent_information?.first_name)) ||
    fullLegalName.split(" ")[0] ||
    person?.nickname ||
    person?.first_name ||
    "Deceased";

  return (
    <div className="px-6 pb-6 space-y-4">
      <InsightsCard title={`${displayName}'s details`} noPadding>
        <InsightsDividerList>
          <InsightsDividerRow
            label="Full legal name"
            value={fullLegalName || NOT_SPECIFIED}
            onClick={citationClick("decedent_information.full_name")}
          />
          <InsightsDividerRow
            label="Date of passing"
            value={formatDate(sanitizeValue(decedent_information?.date_of_death)) || NOT_SPECIFIED}
            onClick={citationClick("decedent_information.date_of_death")}
          />
          <InsightsDividerRow
            label="Place of passing"
            value={
              toTitleCase(sanitizeValue(decedent_information?.place_of_death)) || NOT_SPECIFIED
            }
            onClick={citationClick("decedent_information.place_of_death")}
          />
          <InsightsDividerRow
            label="Residence address"
            value={toTitleCase(
              sanitizeValue(decedent_information?.residence_address) ||
                sanitizeValue(other_details?.residence_address),
            )}
            onClick={citationClick(residencePath)}
          />
          <InsightsDividerRow
            label="SSN"
            value={
              isLoadingSSN ? (
                <span className="text-stone-400">Loading...</span>
              ) : (
                <MaskedValue value={sanitizeValue(decryptedSSN)} />
              )
            }
            onClick={citationClick("decedent_information.ssn")}
          />
          <InsightsDividerRow
            label="Date of birth"
            value={formatDate(sanitizeValue(decedent_information?.date_of_birth)) || NOT_SPECIFIED}
            onClick={citationClick("decedent_information.date_of_birth")}
          />
          <InsightsDividerRow
            label="Place of birth"
            value={toTitleCase(sanitizeValue(other_details?.birthplace)) || NOT_SPECIFIED}
            onClick={citationClick("other_details.birthplace")}
          />
          <InsightsDividerRow
            label="Marital status"
            value={sanitizeValue(decedent_information?.marital_status) || NOT_SPECIFIED}
            onClick={citationClick("decedent_information.marital_status")}
          />
          <InsightsDividerRow
            label="Veteran status"
            value={formatVeteranStatus(other_details?.armed_forces_service)}
            onClick={citationClick("other_details.armed_forces_service")}
          />
          {spouseName && (
            <InsightsDividerRow
              label="Surviving spouse name"
              value={spouseName}
              onClick={citationClick("surviving_spouse.name")}
            />
          )}
          <InsightsDividerRow
            label="Occupation"
            value={toTitleCase(sanitizeValue(other_details?.occupation)) || NOT_SPECIFIED}
            onClick={citationClick("other_details.occupation")}
          />
        </InsightsDividerList>
      </InsightsCard>

      <InsightsCard title="Informant details" noPadding>
        <InsightsDividerList>
          <InsightsDividerRow
            label="Full name"
            value={toTitleCase(sanitizeValue(informant?.name)) || NOT_SPECIFIED}
            onClick={citationClick("informant.name")}
          />
          <InsightsDividerRow
            label={`Relationship to ${displayName}`}
            value={toTitleCase(sanitizeValue(informant?.relationship)) || NOT_SPECIFIED}
            onClick={citationClick("informant.relationship")}
          />
        </InsightsDividerList>
      </InsightsCard>

      <InsightsCard title="Filing details" noPadding>
        <InsightsDividerList>
          <InsightsDividerRow
            label="Certificate number"
            value={sanitizeValue(administrative_information?.certificate_number) || NOT_SPECIFIED}
            onClick={citationClick("administrative_information.certificate_number")}
          />
          <InsightsDividerRow
            label="Date issued"
            value={
              formatDate(sanitizeValue(administrative_information?.date_issued)) || NOT_SPECIFIED
            }
            onClick={citationClick("administrative_information.date_issued")}
          />
          <InsightsDividerRow
            label="State and county filed"
            value={stateCountyFiled || NOT_SPECIFIED}
            onClick={citationClick("administrative_information.state_filed")}
          />
          <InsightsDividerRow
            label="Issuing registrar"
            value={
              toTitleCase(sanitizeValue(administrative_information?.registrar_name)) ||
              NOT_SPECIFIED
            }
            onClick={citationClick("administrative_information.registrar_name")}
          />
        </InsightsDividerList>
      </InsightsCard>

      <InsightsCard title="Additional details" noPadding>
        <InsightsDividerList>
          <InsightsDividerRow
            label="Father's full legal name"
            value={toTitleCase(sanitizeValue(parents?.father_name)) || NOT_SPECIFIED}
            onClick={citationClick("parents.father_name")}
          />
          <InsightsDividerRow
            label="Mother's full legal name"
            value={toTitleCase(sanitizeValue(parents?.mother_name)) || NOT_SPECIFIED}
            onClick={citationClick("parents.mother_name")}
          />
          <InsightsDividerRow
            label="Mother's maiden name"
            value={toTitleCase(sanitizeValue(parents?.mother_maiden_name)) || NOT_SPECIFIED}
            onClick={citationClick("parents.mother_maiden_name")}
          />
          <InsightsDividerRow
            label="Funeral home"
            value={toTitleCase(sanitizeValue(disposition?.funeral_home)) || NOT_SPECIFIED}
            onClick={citationClick("disposition.funeral_home")}
          />
        </InsightsDividerList>
      </InsightsCard>
    </div>
  );
}
